import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { THEME_COLORS } from "../../ThemeConfig";
import { PatientConfig } from "../../axiosConfig";
import { Context } from "../../contexts/Store";
import { AuthModalContext } from "../../contexts/AuthModalContext";
import Lottie from "lottie-react";
import LoaderLottie from "../../assets/lottie/LoaderLottie.json";

export default function ConcernsPage() {
  const { state, dispatch } = useContext(Context);
  const { openLoginModal } = useContext(AuthModalContext);
  const auth = state.user_details?.is_verified;
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = {};
    if (state.user_details?.access_token) {
      headers.Authorization = `Bearer ${state.user_details.access_token}`;
    }
    PatientConfig.get("api/v1/experts/area-of-expertises/", { headers })
      .then((res) => {
        const data = (res.data?.data || []).filter((item) => item.name !== "Other");
        setConcerns(data);
      })
      .catch(() => setConcerns([]))
      .finally(() => setLoading(false));
  }, [state.user_details?.access_token]);

  const handleConcernClick = (item) => {
    if (auth) {
      dispatch({ type: "ADD_SESSION", new_session: { concern: item.id } });
      navigate("/create-session");
    } else {
      openLoginModal?.();
    }
  };

  const handleBookNow = () => {
    if (auth) {
      navigate("/create-session");
    } else {
      openLoginModal?.();
    }
  };

  return (
    <>
      <Main>
        <Section>
          <SectionTag>All Concerns</SectionTag>
          <SectionDesc>
            Explore how we help you navigate various life challenges through specialized expertise.
          </SectionDesc>
          {loading ? (
            <LoaderWrap>
              <Lottie animationData={LoaderLottie} style={{ width: 80, height: 80 }} />
            </LoaderWrap>
          ) : (
            <CardsGrid>
              {concerns.map((item) => (
                <ConcernCard key={item.id} to={`/concerns/${item.id}`}>
                  <ConcernCardAccent />
                  <ConcernCardBody>
                    <ConcernCardTitle>{item.name}</ConcernCardTitle>
                    <ConcernCardSubtitle>{item.title || ""}</ConcernCardSubtitle>
                    <ConcernCardDesc>{item.description || ""}</ConcernCardDesc>
                    <ConcernCardActions>
                      <ConcernLink>Learn more →</ConcernLink>
                      <BookNowBtn
                        onClick={(e) => {
                          e.preventDefault();
                          handleConcernClick(item);
                        }}
                      >
                        Book now
                      </BookNowBtn>
                    </ConcernCardActions>
                  </ConcernCardBody>
                </ConcernCard>
              ))}
            </CardsGrid>
          )}
          <SectionCta>
            <CtaButton onClick={handleBookNow}>Book now</CtaButton>
          </SectionCta>
        </Section>
      </Main>
    </>
  );
}

const Main = styled.main`
  min-height: calc(100vh - 70px);
  background: ${THEME_COLORS.page_bg};
`;
const Section = styled.section`
  padding: 40px 24px 48px;
  max-width: 1100px;
  margin: 0 auto;
  box-sizing: border-box;
  @media all and (max-width: 768px) {
    padding: 32px 16px 40px;
  }
  @media all and (max-width: 480px) {
    padding: 28px 14px 36px;
  }
`;
const SectionTag = styled.h1`
  font-family: "DM_sans_medium";
  font-size: 24px;
  color: ${THEME_COLORS.text_title};
  text-align: center;
  margin: 0 0 10px 0;
  @media all and (max-width: 480px) {
    font-size: 22px;
  }
`;
const SectionDesc = styled.p`
  font-family: "DM_sans";
  font-size: 15px;
  color: ${THEME_COLORS.text_para};
  text-align: center;
  max-width: 560px;
  margin: 0 auto 24px;
  line-height: 1.5;
  @media all and (max-width: 480px) {
    font-size: 14px;
  }
`;
const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;
const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
  @media all and (max-width: 480px) {
    gap: 16px;
    margin-bottom: 20px;
  }
`;
const ConcernCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: ${THEME_COLORS.white};
  border-radius: 16px;
  padding: 0;
  text-decoration: none;
  color: inherit;
  border: 1px solid ${THEME_COLORS.light_300};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.2s ease;
  overflow: hidden;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
    border-color: ${THEME_COLORS.chips_green_container};
  }
`;
const ConcernCardAccent = styled.div`
  height: 4px;
  background: ${THEME_COLORS.chips_green_on_container};
  transition: background 0.2s ease;
`;
const ConcernCardBody = styled.div`
  padding: 24px 24px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  @media all and (max-width: 480px) {
    padding: 20px 18px 18px;
  }
`;
const ConcernCardTitle = styled.h3`
  font-family: "DM_sans_medium";
  font-size: 20px;
  font-weight: 600;
  color: ${THEME_COLORS.text_title};
  margin: 0 0 8px 0;
  line-height: 1.3;
  letter-spacing: -0.01em;
  @media all and (max-width: 480px) {
    font-size: 18px;
  }
`;
const ConcernCardSubtitle = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  margin: 0 0 12px 0;
  line-height: 1.4;
`;
const ConcernCardDesc = styled.p`
  font-family: "DM_sans";
  font-size: 14px;
  color: ${THEME_COLORS.text_para};
  line-height: 1.55;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
  min-height: 3.9em;
`;
const ConcernCardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid ${THEME_COLORS.divider};
`;
const ConcernLink = styled.span`
  font-family: "DM_sans_medium";
  font-size: 14px;
  color: ${THEME_COLORS.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
const BookNowBtn = styled.button`
  padding: 10px 22px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 14px;
  border: none;
  border-radius: 24px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.15s ease;
  box-shadow: 0 2px 8px rgba(29, 166, 135, 0.25);
  &:hover {
    opacity: 0.95;
    transform: scale(1.02);
  }
`;
const SectionCta = styled.div`
  text-align: center;
`;
const CtaButton = styled.button`
  padding: 14px 32px;
  background: ${THEME_COLORS.chips_green_on_container};
  color: ${THEME_COLORS.white};
  font-family: "DM_sans_medium";
  font-size: 16px;
  border: none;
  border-radius: 32px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;
